import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function PainterTableView() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const painter = {
    id: "painters-table-view",
    name: "Painters Table View",
    location: "Table View, Cape Town",
    rating: 4.7,
    reviews: 149,
    specialties: ["Coastal Painting", "Exterior", "Waterproofing", "Wind Resistance"],
    image: "https://public.readdy.ai/ai/img_res/4bd72750fdeaa363286a22ba3817e899.jpg",
    verified: true,
    yearsExperience: 10,
    website: "https://painterstableview.co.za",
    email: "info@painterstableview.co.za",
    description: "Specialized coastal painting services in Table View and surrounding areas. Expert in wind-resistant and marine-grade paints for coastal properties with ocean exposure.",
    services: [
      "Coastal Property Painting",
      "Wind-Resistant Exterior Painting", 
      "Marine-Grade Coating Systems",
      "Waterproofing Services",
      "Salt Air Protection",
      "UV-Resistant Finishes",
      "Storm Damage Restoration",
      "Preventive Maintenance Programs"
    ],
    serviceAreas: ["Table View", "Bloubergstrand", "Milnerton", "Big Bay", "West Coast", "Cape Town", "Western Cape"],
    gallery: [
      "https://readdy.ai/api/search-image?query=Beautiful%20coastal%20home%20painted%20by%20professional%20painters%20in%20Table%20View%2C%20modern%20property%20with%20ocean%20views%2C%20quality%20paint%20finish%2C%20Table%20Mountain%20backdrop%2C%20bright%20natural%20lighting%2C%20clean%20professional%20painting%20work&width=400&height=300&seq=tableview-gallery-1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Exterior%20house%20painting%20project%20in%20Table%20View%20Cape%20Town%2C%20coastal%20home%20with%20fresh%20paint%2C%20professional%20painters%20working%2C%20quality%20exterior%20finish%2C%20ocean%20setting%20with%20mountain%20view%2C%20well-maintained%20property&width=400&height=300&seq=tableview-gallery-2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Wind-resistant%20painting%20project%20in%20Table%20View%2C%20professional%20painters%20working%20on%20coastal%20property%20exterior%2C%20quality%20residential%20paint%20finish%2C%20modern%20coastal%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=tableview-gallery-3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=Marine-grade%20coating%20application%20in%20Table%20View%20coastal%20home%2C%20professional%20painting%20work%2C%20quality%20paint%20finish%2C%20bright%20coastal%20space%2C%20expert%20painting%20techniques&width=400&height=300&seq=tableview-gallery-4&orientation=landscape"
    ],
    testimonials: [
      {
        name: "Michael Roberts",
        rating: 5,
        comment: "Outstanding work on our Table View home. The painters understood the coastal challenges and used specialized paints that have held up perfectly against the strong winds and salt air."
      },
      {
        name: "Jennifer Adams", 
        rating: 5,
        comment: "Professional service from start to finish. They completed our Table View home painting on schedule and the wind-resistant finish has exceeded our expectations."
      },
      {
        name: "Peter van der Walt",
        rating: 4,
        comment: "Great expertise in coastal painting. Our home looks fantastic and we've had no issues with paint deterioration despite the harsh coastal conditions."
      }
    ]
  };

  useEffect(() => {
    document.title = "Painters Table View | Professional House Painting Services Cape Town | Residential & Commercial";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Painters Table View offers professional painting services in Table View, Cape Town. Specializing in residential and commercial painting, coastal properties, and quality finishes. Trusted local painting contractors.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Painters Table View, house painters Table View, painting contractors Cape Town, residential painting, commercial painting services');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-blue-600 to-blue-800">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20coastal%20home%20in%20Table%20View%20Cape%20Town%2C%20stunning%20ocean%20and%20Table%20Mountain%20views%2C%20painters%20in%20clean%20uniforms%20with%20wind-resistant%20equipment%2C%20modern%20coastal%20architecture%2C%20bright%20natural%20lighting%20with%20ocean%20backdrop%2C%20high%20quality%20coastal%20painting%20project&width=1200&height=600&seq=tableview-painters-hero&orientation=landscape)` }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white">
            <div className="flex items-center mb-4">
              <h1 className="text-4xl md:text-5xl font-bold mr-4">{painter.name}</h1>
              {painter.verified && (
                <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                  <i className="ri-shield-check-line mr-1"></i>
                  Verified
                </div>
              )}
            </div>
            
            <p className="text-xl mb-4 flex items-center">
              <i className="ri-map-pin-line mr-2"></i>
              {painter.location}
            </p>
            
            <div className="flex items-center space-x-6 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className={`ri-star-fill ${i < Math.floor(painter.rating) ? 'text-yellow-400' : 'text-gray-400'}`}></i>
                ))}
                <span className="ml-2">{painter.rating} ({painter.reviews} reviews)</span>
              </div>
              <span>{painter.yearsExperience} years experience</span>
            </div>
            
            <div className="flex space-x-4">
              <button 
                onClick={() => navigate('/quote')}
                className="bg-lime-400 text-black px-6 py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'services', label: 'Services' },
              { id: 'gallery', label: 'Gallery' },
              { id: 'reviews', label: 'Reviews' },
              { id: 'contact', label: 'Contact' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">About Painters Table View - Your Coastal Painting Specialists</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  {painter.description} With over 10 years of specialized experience in coastal painting applications, we have developed unique expertise in protecting and beautifying properties exposed to the challenging West Coast marine environment.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Wind-Resistant Painting Technologies</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Table View is renowned for its strong coastal winds, particularly the powerful south-easter that can reach extreme velocities. These conditions require specialized painting techniques and materials that can withstand constant wind pressure and the abrasive effects of wind-borne sand and salt particles. Our team has developed proprietary application methods that ensure paint adhesion and longevity even in these challenging conditions, using flexible coating systems that move with building materials as they expand and contract.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Marine-Grade Protection Systems</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The proximity to the Atlantic Ocean means that Table View properties are constantly exposed to salt-laden air that can cause rapid deterioration of standard paint systems. We exclusively use marine-grade paints and protective coatings specifically formulated for harsh coastal environments. These advanced coating systems provide superior resistance to salt corrosion, UV radiation, and moisture penetration, ensuring your investment is protected for years to come while maintaining aesthetic appeal.
                </p>
                
                <h3 className="text-2xl font-semibent text-gray-900 mb-4">Specialized Surface Preparation for Coastal Conditions</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Proper surface preparation is absolutely critical for coastal painting projects in Table View. Our comprehensive preparation process includes thorough cleaning to remove salt deposits, sanding to create optimal adhesion surfaces, and the application of specialized primers designed for marine environments. We pay particular attention to areas most vulnerable to coastal damage, such as window frames, door surrounds, and any metal fixtures that may be prone to corrosion from salt air exposure.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">UV Protection and Color Retention</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The intense UV radiation experienced along the West Coast can cause rapid fading and degradation of standard paint systems. Our UV-resistant coating technologies maintain color integrity and surface protection even under extreme sun exposure. We use advanced pigment systems and UV-blocking additives that preserve the vibrancy and appearance of your property's exterior surfaces, ensuring long-lasting beauty that withstands the harsh coastal sun.
                </p>
                
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Preventive Maintenance Programs</h3>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Understanding the ongoing challenges of coastal property maintenance, we offer comprehensive preventive maintenance programs designed specifically for Table View properties. These programs include regular inspections, touch-up services, and protective treatments that extend the life of your paint system while maintaining optimal appearance. Our proactive approach helps prevent minor issues from becoming major problems, saving you money and ensuring your property always looks its best.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {painter.specialties.map((specialty, index) => (
                        <span key={index} className="bg-lime-100 text-lime-800 px-3 py-1 rounded-full text-sm font-medium">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Service Areas</h3>
                    <div className="space-y-2">
                      {painter.serviceAreas.slice(0, 4).map((area, index) => (
                        <div key={index} className="flex items-center text-gray-700">
                          <i className="ri-map-pin-line mr-2 text-lime-500"></i>
                          {area}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-6 rounded-lg sticky top-6">
                  <div className="mb-6">
                    <img 
                      src="https://readdy.ai/api/search-image?query=Professional%20painting%20team%20working%20on%20residential%20home%20in%20Table%20View%20Cape%20Town%2C%20northern%20suburbs%20coastal%20property%20with%20Table%20Mountain%20views%2C%20painters%20in%20uniform%20with%20quality%20equipment%2C%20modern%20suburban%20architecture%2C%20organized%20work%20site&width=400&height=300&seq=tableview-sidebar-image&orientation=landscape"
                      alt="Professional painters Table View residential house painting services Cape Town northern suburbs Table Mountain"
                      className="w-full h-48 object-cover object-top rounded-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <i className="ri-mail-line mr-3 text-lime-500"></i>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <i className="ri-global-line mr-3 text-lime-500"></i>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        Visit Website
                      </a>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => navigate('/quote')}
                    className="w-full mt-6 bg-lime-400 text-black py-3 rounded-lg font-semibold hover:bg-lime-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Get Free Quote
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Specialized Coastal Painting Services in Table View</h2>
              
              <div className="mb-12">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">Advanced Coastal Protection Solutions</h3>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Our specialized coastal painting services are specifically designed for the extreme conditions experienced in Table View and surrounding West Coast areas. From wind-resistant exterior coatings to salt-air protection systems, we provide comprehensive solutions that protect and beautify your coastal property while withstanding the harshest marine environment.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {painter.services.map((service, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center mb-4">
                      <i className="ri-brush-line text-2xl text-lime-500 mr-3"></i>
                      <h3 className="text-lg font-semibold text-gray-900">{service}</h3>
                    </div>
                    <p className="text-gray-600">Professional {service.toLowerCase()} with marine-grade materials and expert coastal application techniques.</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Wind-Resistant Exterior Solutions</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our wind-resistant exterior painting systems are specifically engineered for Table View's challenging wind conditions. We use flexible coating technologies that maintain adhesion and integrity even under extreme wind pressure, preventing the cracking and peeling commonly experienced with standard paint systems in coastal areas.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    The application process includes specialized techniques for working in windy conditions, ensuring optimal coverage and finish quality regardless of weather conditions. Our team is trained in coastal application methods that account for wind patterns and environmental factors unique to the Table View area.
                  </p>
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">Marine-Grade Coating Systems</h3>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    Our marine-grade coating systems provide the ultimate protection against salt air, UV radiation, and moisture penetration. These advanced systems include multiple layers of specialized primers, base coats, and topcoats, each formulated for specific aspects of coastal protection while maintaining superior aesthetic appeal.
                  </p>
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    We also offer specialized waterproofing services that create comprehensive moisture barriers, preventing water infiltration that can lead to structural damage and paint failure. Our waterproofing systems are designed to work in harmony with our paint systems, providing complete protection for your coastal property.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Recent Coastal Painting Projects in Table View</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Explore our portfolio of recent coastal painting projects throughout Table View and surrounding West Coast areas. Each project demonstrates our expertise in wind-resistant and marine-grade coating applications designed for extreme coastal conditions.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {painter.gallery.map((image, index) => (
                  <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <img 
                      src={image} 
                      alt={`${painter.name} project ${index + 1}`}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Table View Project {index + 1}</h3>
                      <p className="text-gray-600 text-sm">Professional coastal painting with wind-resistant and marine-grade materials designed for extreme coastal conditions.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Customer Reviews and Testimonials</h2>
              <p className="text-gray-700 mb-12 leading-relaxed">
                Read what our satisfied customers throughout Table View and the West Coast have to say about our specialized coastal painting services. Our commitment to excellence in extreme coastal conditions is reflected in these genuine reviews.
              </p>
              
              <div className="space-y-6">
                {painter.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`ri-star-fill ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}></i>
                        ))}
                      </div>
                      <span className="ml-3 font-semibold text-gray-900">{testimonial.name}</span>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{testimonial.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Painters Table View</h2>
                <p className="text-gray-700 mb-8 leading-relaxed">
                  Ready to protect your Table View property with specialized coastal painting services? Contact us today for a free consultation and quote. We're here to help you achieve durable, beautiful results that withstand the challenging coastal environment.
                </p>
                
                <div className="space-y-6">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Email</h3>
                      <a href={`mailto:${painter.email}`} className="text-gray-700 hover:text-lime-600">
                        {painter.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <i className="ri-global-line text-2xl text-lime-500 mr-4"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Website</h3>
                      <a href={painter.website} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-lime-600">
                        {painter.website}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <i className="ri-map-pin-line text-2xl text-lime-500 mr-4 mt-1"></i>
                    <div>
                      <h3 className="font-semibold text-gray-900">Service Area</h3>
                      <p className="text-gray-700">{painter.location} and surrounding West Coast areas</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Get Your Free Coastal Assessment</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Request a free, no-obligation assessment for your Table View coastal painting project. Our experienced team will evaluate your property's specific coastal challenges and provide detailed recommendations.
                </p>
                <button 
                  onClick={() => navigate('/quote')}
                  className="w-full bg-lime-400 text-black py-4 rounded-lg font-semibold hover:bg-lime-500 transition-colors text-lg whitespace-nowrap cursor-pointer"
                >
                  Request Free Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
